/**
 * PaymentMethodTemplatesPage - Admin Payment Method Template Management
 *
 * Displays and manages payment method templates.
 * Includes create, edit, delete, and broadcast functionality.
 */

import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { adminService } from '../../services/admin.service';
import EmojiPicker from '../../components/EmojiPicker';

interface PaymentMethodTemplate {
  id: string;
  name: string;
  logoUrl: string;
  createdAt: string;
  updatedAt: string;
}

// Icons
const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const CreditCardIcon = () => (
  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

// ===========================================
// PaymentMethodTemplateForm Component
// ===========================================
interface PaymentMethodTemplateFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { name: string; logoUrl: string }) => Promise<void>;
  editTemplate?: PaymentMethodTemplate | null;
}

const PaymentMethodTemplateForm = ({
  isOpen,
  onClose,
  onSave,
  editTemplate,
}: PaymentMethodTemplateFormProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (editTemplate) {
      setName(editTemplate.name);
      setLogoUrl(editTemplate.logoUrl || '');
    } else {
      setName('');
      setLogoUrl('');
    }
  }, [editTemplate, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      await onSave({
        name: name.trim(),
        logoUrl: logoUrl.trim() || '💳',
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {editTemplate
            ? t('admin.paymentMethodTemplates.edit', 'Modifier le template')
            : t('admin.paymentMethodTemplates.create', 'Créer un template')}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('admin.paymentMethodTemplates.form.name', 'Nom')} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              placeholder={t('admin.paymentMethodTemplates.form.namePlaceholder', 'Ex: Carte bancaire, PayPal...')}
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('admin.paymentMethodTemplates.form.icon', 'Icône')}
            </label>
            <div className="relative">
              <button
                ref={emojiButtonRef}
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white dark:bg-slate-700 text-left flex items-center gap-2"
              >
                {logoUrl ? (
                  <>
                    <span className="text-2xl">{logoUrl}</span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {t('admin.paymentMethodTemplates.form.changeIcon', "Changer l'icône")}
                    </span>
                  </>
                ) : (
                  <span className="text-slate-400 dark:text-slate-500">
                    {t('admin.paymentMethodTemplates.form.selectIcon', 'Choisir une icône...')}
                  </span>
                )}
              </button>

              {showEmojiPicker && (
                <div className="absolute top-full left-0 mt-2 z-50">
                  <EmojiPicker
                    value={logoUrl}
                    onChange={(emoji) => setLogoUrl(emoji)}
                    onClose={() => setShowEmojiPicker(false)}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===========================================
// DeleteConfirmDialog Component
// ===========================================
interface DeleteConfirmDialogProps {
  isOpen: boolean;
  template: PaymentMethodTemplate | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteConfirmDialog = ({ isOpen, template, onClose, onConfirm }: DeleteConfirmDialogProps) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen || !template) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {t('admin.paymentMethodTemplates.delete', 'Supprimer le template')}
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mb-4">
          {t(
            'admin.paymentMethodTemplates.deleteConfirm',
            'Êtes-vous sûr de vouloir supprimer le template "{{name}}" ?',
            { name: template.name }
          )}
        </p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isDeleting ? t('common.loading') : t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===========================================
// Main Component
// ===========================================
export function PaymentMethodTemplatesPage() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<PaymentMethodTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState<PaymentMethodTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PaymentMethodTemplate | null>(null);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getPaymentMethodTemplates();
      setTemplates(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleAddNew = () => {
    setEditTemplate(null);
    setShowFormModal(true);
  };

  const handleEdit = (template: PaymentMethodTemplate) => {
    setEditTemplate(template);
    setShowFormModal(true);
  };

  const handleDelete = (template: PaymentMethodTemplate) => {
    setDeleteTarget(template);
  };

  const handleSave = async (data: { name: string; logoUrl: string }) => {
    if (editTemplate) {
      await adminService.updatePaymentMethodTemplate(editTemplate.id, data);
    } else {
      await adminService.createPaymentMethodTemplate(data);
    }
    fetchTemplates();
  };

  const handleConfirmDelete = async () => {
    if (deleteTarget) {
      await adminService.deletePaymentMethodTemplate(deleteTarget.id);
      fetchTemplates();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">
            {t('admin.paymentMethodTemplates.title', 'Templates de modes de paiement')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm sm:text-base">
            {t('admin.paymentMethodTemplates.subtitle', 'Gérez les templates de modes de paiement par défaut')}
          </p>
        </div>
        <button
          onClick={handleAddNew}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
        >
          <PlusIcon />
          <span className="hidden sm:inline">{t('admin.paymentMethodTemplates.create', 'Nouveau template')}</span>
          <span className="sm:hidden">{t('common.add', 'Ajouter')}</span>
        </button>
      </div>

      {/* Error display */}
      {error && (
        <div className="bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-lg p-4 flex items-center justify-between">
          <p className="text-rose-700 dark:text-rose-400">{error}</p>
          <button onClick={() => setError(null)} className="text-rose-500 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300">
            ✕
          </button>
        </div>
      )}

      {/* Loading */}
      {isLoading && templates.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && templates.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
            <CreditCardIcon />
          </div>
          <h2 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            {t('admin.paymentMethodTemplates.empty.title', 'Aucun template')}
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            {t(
              'admin.paymentMethodTemplates.empty.message',
              'Créez votre premier template pour définir les modes de paiement par défaut.'
            )}
          </p>
          <button
            onClick={handleAddNew}
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
          >
            <PlusIcon />
            <span>{t('admin.paymentMethodTemplates.empty.create', 'Créer un template')}</span>
          </button>
        </div>
      ) : (
        /* Template list */
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-violet-50 dark:bg-violet-900/30 rounded-t-xl">
            <h2 className="font-semibold text-violet-700 dark:text-violet-400 flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-violet-500" />
              {t('admin.paymentMethodTemplates.listTitle', 'Modes de paiement')}
              <span className="text-sm font-normal text-violet-500 dark:text-violet-400">({templates.length})</span>
            </h2>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {templates.map((template) => (
              <div
                key={template.id}
                className="group flex items-center py-3 px-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
              >
                {/* Logo/Icon */}
                <span className="mr-4 text-2xl flex-shrink-0">{template.logoUrl}</span>

                {/* Name */}
                <span className="flex-1 font-medium text-slate-700 dark:text-slate-300">{template.name}</span>

                {/* Actions - always visible on mobile */}
                <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleEdit(template)}
                    className="p-1.5 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/40 text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    title={t('common.edit', 'Modifier')}
                  >
                    <PencilIcon />
                  </button>
                  <button
                    onClick={() => handleDelete(template)}
                    className="p-1.5 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/40 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
                    title={t('common.delete', 'Supprimer')}
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      <PaymentMethodTemplateForm
        isOpen={showFormModal}
        onClose={() => {
          setShowFormModal(false);
          setEditTemplate(null);
        }}
        onSave={handleSave}
        editTemplate={editTemplate}
      />

      <DeleteConfirmDialog
        isOpen={!!deleteTarget}
        template={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

export default PaymentMethodTemplatesPage;
